import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Calendar, dateFnsLocalizer, Views } from 'react-big-calendar';
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import startOfWeek from 'date-fns/startOfWeek';
import getDay from 'date-fns/getDay';
import enUS from 'date-fns/locale/en-US';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { format as formatDate } from 'date-fns';
import { format as formatDateKey } from 'date-fns';
import { CalendarDays } from 'lucide-react';
import { getStatusColor } from '../utils/statusColors';
import './AdminDashboard.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const locales = { 'en-US': enUS };
const localizer = dateFnsLocalizer({
    format,
    parse,
    startOfWeek,
    getDay,
    locales,
});

function DriverCalendar() {
    const [bookings, setBookings] = useState([]);
    const navigate = useNavigate();
    const driverName = localStorage.getItem('driverName') || 'Driver';

    const [view, setView] = useState(window.innerWidth < 768 ? 'day' : 'month');

    useEffect(() => {
        const handleResize = () => {
            setView(window.innerWidth < 768 ? 'day' : 'month');
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const fetchBookings = useCallback(async () => {
        const token = localStorage.getItem('driverToken');
        if (!token) {
            navigate('/login');
            return;
        }
        try {
            const response = await axios.get(`${API_URL}/api/driver/bookings`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setBookings(response.data);
        } catch (err) {
            // silent fail on calendar view, dashboard already surfaces errors
        }
    }, [navigate]);

    useEffect(() => {
        fetchBookings();
        const interval = setInterval(fetchBookings, 20000);
        return () => clearInterval(interval);
    }, [fetchBookings]);

    const handleLogout = () => {
        localStorage.removeItem('driverToken');
        localStorage.removeItem('driverName');
        navigate('/login');
    };

    const events = bookings.map((b) => {
        const start = new Date(b.pickup_datetime);
        const end = new Date(start.getTime() + 30 * 60000);
        return {
            id: b.id,
            title: `#${b.id} ${b.customer_name} — ${b.pickup_location} → ${b.destination}`,
            start,
            end,
            status: b.status,
        };
    });

    const dailyCounts = {};
    bookings.forEach((b) => {
        const dateKey = formatDateKey(new Date(b.pickup_datetime), 'yyyy-MM-dd');
        dailyCounts[dateKey] = (dailyCounts[dateKey] || 0) + 1;
    });

    const monthBadgeEvents = Object.entries(dailyCounts).map(([dateKey, count]) => {
        const date = new Date(dateKey + 'T00:00:00');
        return {
            id: `badge-${dateKey}`,
            title: String(count),
            start: date,
            end: date,
            isBadge: true,
        };
    });

    const displayEvents = view === Views.MONTH ? monthBadgeEvents : events;

    const eventStyleGetter = (event) => {
        if (event.isBadge) {
            return {
                style: {
                    backgroundColor: 'var(--primary)',
                    color: 'var(--white)',
                    borderRadius: '50%',
                    width: '28px',
                    height: '28px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.8125rem',
                    fontWeight: 700,
                    margin: '0 auto',
                    padding: 0,
                    border: 'none',
                },
            };
        }
        return {
            style: {
                backgroundColor: getStatusColor(event.status),
                borderRadius: '6px',
                color: '#fff',
                border: 'none',
                fontSize: '0.8125rem',
            },
        };
    };

    const calendarFormats = {
        dateFormat: 'd',
        dayFormat: 'EEE d',
        monthHeaderFormat: (date) => formatDate(date, 'MMMM yyyy'),
        dayHeaderFormat: (date) => formatDate(date, 'EEEE, MMMM d'),
        dayRangeHeaderFormat: ({ start, end }) =>
            `${formatDate(start, 'MMMM d')} – ${formatDate(end, 'MMMM d, yyyy')}`,
        agendaDateFormat: (date) => formatDate(date, 'EEE, MMM d'),
        agendaHeaderFormat: ({ start, end }) =>
            `${formatDate(start, 'MMMM d')} – ${formatDate(end, 'MMMM d, yyyy')}`,
    };

    return (
        <div className="dashboard-page driver-compact">
            <div className="dashboard-container" style={{ maxWidth: '1100px' }}>
                <div className="dashboard-header">
                    <div>
                        <h1><CalendarDays size={22} className="page-icon" /> My Calendar</h1>
                        <p>{driverName}'s rides + still-available ones</p>
                    </div>
                </div>

                <div className="filters" style={{ marginBottom: '20px', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <Link to="/driver/dashboard" style={{ textDecoration: 'none' }}>My Rides</Link>
                        <Link to="/driver/calendar" className="active" style={{ textDecoration: 'none' }}>Calendar</Link>
                    </div>
                    <button onClick={handleLogout} className="logout-btn">Logout</button>
                </div>

                <div className="calendar-panel">
                    <Calendar
                        localizer={localizer}
                        events={displayEvents}
                        startAccessor="start"
                        endAccessor="end"
                        style={{ height: 'calc(100vh - 220px)' }}
                        eventPropGetter={eventStyleGetter}
                        view={view}
                        onView={setView}
                        views={window.innerWidth < 768 ? [Views.MONTH, Views.DAY, Views.AGENDA] : [Views.MONTH, Views.WEEK, Views.DAY, Views.AGENDA]}                        formats={calendarFormats}
                        step={60}
                        timeslots={1}
                        min={new Date(2000, 0, 1, 6, 0)}
                        max={new Date(2000, 0, 1, 23, 59)}
                    />
                </div>
            </div>
        </div>
    );
}

export default DriverCalendar;